import React from 'react';
import { LogOut } from 'lucide-react';

/* Mobile Nav Drawer -- extracted verbatim from App.jsx (C1 refactor,
   behavior-preserving). Rendered by BlitzHeader on mobile. Self-contained:
   uses only lucide LogOut + native HTML. No useTheme() call despite the stale
   in-body comment (the theme toggle was moved out 2026-05-23). */
export function MobileNav({ pages, current, userAccess, onNav, onLogout, onClose }) {
  // H1 Rev 2: useTheme() called directly inside MobileNav (NOT prop-drilled from
  // BlitzHeader → MobileNav). MobileNav already lives inside ThemeContext.Provider
  // (mounted in App() root at line ~6321), so this works. Prop drilling would cascade
  // to 5+ callsites which is the wrong tradeoff for a single button.
  // v9.05: Grouped mobile navigation
  const MOBILE_GROUPS = [
    { title: null, items: [{ key: "overview", label: "📊 Dashboard", color: "#6366F1" }] },
    { title: "Finance", items: [
      { key: "payments", label: "💳 Payments", color: "#0EA5E9" },
      { key: "customers", label: "👥 Customer Payments", color: "#0EA5E9" },
      { key: "dailycalcs", label: "🧮 Daily Calcs", color: "#EF4444" },
      { key: "genbalances", label: "💎 Gen Balances", color: "#10B981" },
      { key: "invoicegen", label: "🧾 Invoice Generator", color: "#F97316" },
    ]},
    { title: "Deals", items: [
      { key: "crg", label: "📋 CRG Deals", color: "#F59E0B" },
      { key: "deals", label: "🏷️ Offers", color: "#10B981" },
      { key: "dailycap", label: "📈 Daily Cap", color: "#8B5CF6" },
    ]},
    // v32.16.24 fix: 'brokers' (added 2026-06-03) и 'ai' (AI Traffic Advisor, v32.13)
    // были в desktop nav (BlitzHeader Info dropdown) но забыты здесь → раздел был
    // недоступен на мобиле ("can't see this page in the android version"). Восстановлен
    // паритет с desktop NAV_GROUPS → Info. Порядок и labels как на desktop.
    { title: "Info", items: [
      { key: "partners", label: "🎯 Partners", color: "#EC4899" },
      { key: "roster", label: "👥 Affiliates & Brokers", color: "#14B8A6" },
      { key: "brokers", label: "🏦 Brokers", color: "#52606d" },
      { key: "phonecheck", label: "Phone Checker", color: "#2563EB" },
      { key: "monthlystats", label: "📊 Blitz Report", color: "#6366F1" },
      { key: "ftdsinfo", label: "📈 FTDs Info", color: "#10B981" },
      { key: "ai", label: "🤖 AI Advisor", color: "#A855F7" },
    ]},
    { title: "AI Space", items: [
      // v32.16.65 (2026-06-10): Analytics entry removed — вкладка убрана из CRM (page dormant в src/pages/Analytics/).
      { key: "autob2026", label: "🤖 AUTO-B2026", color: "#A855F7" },
      { key: "injections", label: "💉 Injections", color: "#10B981" },
      // Radar перенесён из первой группы (рядом с Dashboard) сюда — последним в AI Space (симметрия с desktop).
      { key: "radar", label: "🛰️ Radar", color: "#06B6D4" },
    ]},
    { title: null, items: [
      { key: "settings", label: "⚙️ Settings", color: "#64748B" },
      ...(pages.some(p => p.key === "admin") ? [{ key: "admin", label: "🔒 Admin", color: "#DC2626" }] : []),
    ]},
  ];

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998, background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 280, background: "#FFFFFF", boxShadow: "-4px 0 30px rgba(0,0,0,0.15)", zIndex: 9999, padding: "20px 0", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "0 20px 16px", borderBottom: "1px solid #E2E8F0", marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Menu</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={() => { window.dispatchEvent(new CustomEvent('blitz-refresh')); onClose(); }}
              style={{ display:"flex", alignItems:"center", gap:4, background:"rgba(16,185,129,0.12)", border:"1.5px solid rgba(16,185,129,0.35)", color:"#10B981", cursor:"pointer", fontSize:13, fontWeight:700, padding:"6px 12px", borderRadius:8 }}>
              🔄 Refresh
            </button>
            <button onClick={() => { window.dispatchEvent(new CustomEvent('blitz-revert')); onClose(); }}
              style={{ display:"flex", alignItems:"center", gap:4, background:"rgba(239,68,68,0.12)", border:"1.5px solid rgba(239,68,68,0.35)", color:"#F87171", cursor:"pointer", fontSize:13, fontWeight:700, padding:"6px 12px", borderRadius:8 }}>
              ↩ Revert
            </button>
            <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#64748B", padding: 4 }}>✕</button>
          </div>
        </div>
        {/* Theme toggle moved 2026-05-23 — was here in mobile drawer, but light theme
            currently only supported on Injections (PR1). Other CRM pages stay dark-only
            until PR2 (Analytics) + PR3 (AutoB2026) ship. Toggle now lives ВНУТРИ
            Injections page header, visible только когда theme matters. */}
        {MOBILE_GROUPS.map((group, gi) => {
          const visible = group.items.filter(i => i.key === "admin" ? pages.some(p => p.key === "admin") : (userAccess || []).includes(i.key));
          if (visible.length === 0) return null;
          return (
            <div key={gi}>
              {group.title && (
                <div style={{ padding: "12px 24px 4px", fontSize: 10, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 1.2 }}>
                  {group.title}
                </div>
              )}
              {visible.map(pg => {
                const isActive = current === pg.key;
                return (
                  <button key={pg.key} onClick={() => { onNav(pg.key); onClose(); }}
                    style={{
                      display: "block", width: "100%", padding: "12px 24px", border: "none",
                      background: isActive ? `${pg.color}12` : "transparent",
                      color: isActive ? pg.color : "#334155", fontSize: 14, fontWeight: isActive ? 700 : 500,
                      cursor: "pointer", textAlign: "left",
                      borderLeft: isActive ? `4px solid ${pg.color}` : "4px solid transparent",
                      transition: "all 0.15s",
                    }}>
                    {pg.label}
                  </button>
                );
              })}
              {gi < MOBILE_GROUPS.length - 1 && visible.length > 0 && (
                <div style={{ height: 1, background: "#F1F5F9", margin: "6px 20px" }} />
              )}
            </div>
          );
        })}
        {/* v31.88: Logout button — отсутствовал на mobile drawer, на desktop был в header (App.jsx:2019). */}
        {onLogout && (
          <>
            <div style={{ height: 1, background: "#E2E8F0", margin: "12px 20px 8px" }} />
            <button onClick={() => { onLogout(); onClose(); }}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                width: "calc(100% - 40px)", margin: "4px 20px 8px",
                padding: "12px 16px", border: "1.5px solid rgba(239,68,68,0.35)",
                background: "rgba(239,68,68,0.08)", color: "#DC2626",
                cursor: "pointer", textAlign: "left",
                fontSize: 14, fontWeight: 700, borderRadius: 10,
                fontFamily: "inherit",
              }}>
              <LogOut size={16} strokeWidth={2} /> Logout
            </button>
          </>
        )}
      </div>
    </div>
  );
}
